import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  Paper,
  Typography
} from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridPaginationModel,
  GridToolbar
} from '@mui/x-data-grid';
import { UserLogEntry, UserLogResponse } from '../types';

interface LogActivityTableProps {
  title: string;
  loading: boolean;
  error: string | null;
  data: UserLogResponse | null;
}

function formatTimestamp(value?: string | null) {
  if (!value) {
    return '';
  }

  try {
    return new Date(value).toLocaleString();
  } catch (_) {
    return value;
  }
}

const LogActivityTable: React.FC<LogActivityTableProps> = ({
  title,
  loading,
  error,
  data
}) => {
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 10
  });

  const rows = useMemo(() => (data?.entries || []).map((entry) => ({
    ...entry,
    emailsJoined: entry.emails.join(', ')
  })), [data]);

  const columns = useMemo<GridColDef<UserLogEntry & { emailsJoined: string }>[]>(() => [
    {
      field: 'timestamp',
      headerName: 'Time',
      minWidth: 190,
      flex: 0.9,
      renderCell: (params) => (
        <Box sx={{ py: 1, whiteSpace: 'normal', lineHeight: 1.35 }}>
          {formatTimestamp(params.value)}
        </Box>
      )
    },
    {
      field: 'action',
      headerName: 'Action',
      minWidth: 220,
      flex: 1,
      renderCell: (params) => (
        <Box sx={{ py: 1, fontWeight: 600, whiteSpace: 'normal', lineHeight: 1.35 }}>
          {params.value}
        </Box>
      )
    },
    {
      field: 'level',
      headerName: 'Level',
      width: 110,
      renderCell: (params) => (
        <Chip
          size="small"
          label={String(params.value || '').toUpperCase()}
          color={
            params.value === 'error'
              ? 'error'
              : params.value === 'warn'
                ? 'warning'
                : 'default'
          }
          variant={params.value === 'info' ? 'outlined' : 'filled'}
          sx={{ mt: 1 }}
        />
      )
    },
    {
      field: 'requestId',
      headerName: 'Request ID',
      minWidth: 180,
      flex: 0.9
    },
    {
      field: 'route',
      headerName: 'Route',
      minWidth: 170,
      flex: 0.8
    },
    {
      field: 'addinVersion',
      headerName: 'Plugin',
      width: 110
    },
    {
      field: 'emailsJoined',
      headerName: 'Emails',
      minWidth: 220,
      flex: 1,
      renderCell: (params) => (
        <Box sx={{ py: 1, whiteSpace: 'normal', lineHeight: 1.35 }}>
          {params.value}
        </Box>
      )
    },
    {
      field: 'message',
      headerName: 'Message',
      minWidth: 260,
      flex: 1.2,
      renderCell: (params) => (
        <Box sx={{ py: 1, whiteSpace: 'normal', lineHeight: 1.35 }}>
          {params.value}
        </Box>
      )
    },
    {
      field: 'raw',
      headerName: 'Raw log',
      minWidth: 360,
      flex: 1.8,
      renderCell: (params) => (
        <Box
          sx={{
            py: 1,
            fontFamily: 'monospace',
            fontSize: 12,
            whiteSpace: 'normal',
            lineHeight: 1.4,
            wordBreak: 'break-word'
          }}
        >
          {params.value}
        </Box>
      )
    }
  ], []);

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="h6">{title}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
        Search, sort, and page through the matched log lines for this mailbox. Default sorting is newest first.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      {data?.missing && !error && (
        <Alert severity="info" sx={{ mt: 2 }}>
          Log directory was not found: {data.logDirectory}
        </Alert>
      )}

      {!error && data && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
          <Chip label={`${data.count} rows`} size="small" variant="outlined" />
          <Chip label={data.truncated ? 'Result capped' : 'Full result'} size="small" variant="outlined" />
          <Chip label={`Loaded ${formatTimestamp(data.loadedAt)}`} size="small" variant="outlined" />
        </Box>
      )}

      <Box sx={{ mt: 2 }}>
        <DataGrid
          autoHeight
          rows={rows}
          columns={columns}
          loading={loading}
          getRowId={(row) => row.id}
          getRowHeight={() => 'auto'}
          slots={{ toolbar: GridToolbar }}
          slotProps={{
            toolbar: {
              showQuickFilter: true
            }
          }}
          initialState={{
            sorting: {
              sortModel: [{ field: 'timestamp', sort: 'desc' }]
            }
          }}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50, 100]}
          disableRowSelectionOnClick
        />
      </Box>
    </Paper>
  );
};

export default LogActivityTable;
